import { FC, ReactNode, useContext } from "react";
import { Button, Result } from "antd";
import { useLocation, useNavigate } from "./AppRouter";
import { AbilityContext } from "../components/shared/AbilityContext";
import { getPayload } from "../utils/auth";
import { resolveModuleSubjects } from "./moduleAccess";

interface Props {
  children: ReactNode;
}

/**
 * El token trae los módulos del usuario, que es de donde sale el `ability`.
 * Si viene sin esa lista es un token roto, no un usuario sin permisos, y ahí
 * conviene dejar pasar: encerrar a todo el mundo fuera de la aplicación por un
 * fallo de decodificación es peor que el agujero que abre.
 */
const tokenDeclaresModules = (): boolean => {
  try {
    return Array.isArray(getPayload()?.permisos?.modulos);
  } catch {
    return false;
  }
};

/**
 * `ProtectedRoute` solo valida que haya sesión, así que hasta ahora cualquier
 * ruta de `/home` se montaba escribiendo la URL aunque al usuario le hubieran
 * quitado el módulo: desaparecía del menú, pero la pantalla seguía viva y solo
 * se perdían los botones que ya tenían su propio `Can`.
 *
 * Esto lo cierra para todos los módulos a la vez. Va envolviendo el `Outlet`
 * de `home.tsx` en lugar de ruta por ruta, para que el 403 se pinte dentro del
 * layout —con su menú y su cabecera— y la persona pueda irse a otro sitio.
 *
 * El `ability` ya está poblado desde el JWT antes de que monte cualquier ruta
 * (`updateAbility` en index.tsx, y otra vez en `ProtectedRoute` después del
 * auto-login), así que aquí basta con leerlo.
 */
const ModuleGuard: FC<Props> = ({ children }) => {
  const ability: any = useContext(AbilityContext);
  const { pathname } = useLocation();
  const push = useNavigate();

  const subjects = resolveModuleSubjects(pathname);

  const allowed =
    !subjects ||
    !tokenDeclaresModules() ||
    subjects.some((subject) => ability?.can("ACCESS", subject));

  if (allowed) return <>{children}</>;

  return (
    <Result
      status="403"
      title="No tienes acceso a este módulo"
      subTitle="Solicita la asignación del permiso y vuelve a intentarlo."
      extra={
        <Button
          type="primary"
          shape="round"
          size="small"
          onClick={() => push("/home/dashboard")}
        >
          Ir al inicio
        </Button>
      }
    />
  );
};

export default ModuleGuard;
